const BOOLEAN_KEYS = ["required", "multiple", "mark_circle"];

export const getFieldSettings = (field) => {
  const source = field?.field_settings;
  if (!source || typeof source !== "object" || Array.isArray(source)) return {};
  const settings = {};
  BOOLEAN_KEYS.forEach((key) => {
    if (typeof source[key] === "boolean") settings[key] = source[key];
  });
  if (Array.isArray(source.def_value)) {
    settings.def_value = [
      ...new Set(
        source.def_value.filter(
          (id) => typeof id === "string" && id.length > 0,
        ),
      ),
    ];
  }
  return settings;
};

export const applyFieldSettings = (field) => {
  const settings = getFieldSettings(field);
  return Object.keys(settings).length ? { ...field, ...settings } : field;
};

export const patchFieldSettings = (field, patch = {}) => {
  const settings = { ...getFieldSettings(field) };
  [...BOOLEAN_KEYS, "def_value"].forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(patch, key)) return;
    if (patch[key] === null) delete settings[key];
    else if (key === "def_value" && Array.isArray(patch[key])) {
      settings[key] = [
        ...new Set(
          patch[key].filter((id) => typeof id === "string" && id.length > 0),
        ),
      ];
    } else if (BOOLEAN_KEYS.includes(key) && typeof patch[key] === "boolean") {
      settings[key] = patch[key];
    }
  });
  return { ...field, field_settings: settings };
};

export const removeInheritedFieldSettings = (field, sharedField) => {
  // Do not remove overrides before the shared definition is available.
  if (!sharedField) return field;

  const settings = { ...getFieldSettings(field) };

  BOOLEAN_KEYS.forEach((key) => {
    if (
      Object.prototype.hasOwnProperty.call(settings, key) &&
      settings[key] === Boolean(sharedField[key])
    ) {
      delete settings[key];
    }
  });

  if (Object.prototype.hasOwnProperty.call(settings, "def_value")) {
    const sharedDefaults = getFieldSettings({
      field_settings: {
        def_value: Array.isArray(sharedField.def_value)
          ? sharedField.def_value
          : [],
      },
    }).def_value;

    // Default selections are compared without depending on their order.
    const sharedIds = new Set(sharedDefaults);
    if (
      settings.def_value.length === sharedIds.size &&
      settings.def_value.every((id) => sharedIds.has(id))
    ) {
      delete settings.def_value;
    }
  }

  return { ...field, field_settings: settings };
};

export const getEditorInstanceLabel = (field) => {
  const zero = (value) =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value)) &&
    Number(value) === 0;
  const settingsRecord =
    field?.hidden === true &&
    field?.field_settings != null &&
    !String(field?.field_id || "").startsWith("generic_") &&
    [
      [field.x, field.rect?.X],
      [field.y, field.rect?.Y],
      [field.width, field.rect?.W],
      [field.height, field.rect?.H],
    ].every(([value, rectValue]) => zero(value ?? rectValue));
  return settingsRecord
    ? (field.field_label ?? field.label ?? "")
    : field?.field_label ||
        field?.label ||
        field?.name ||
        field?.pdf_field_name ||
        "";
};

export const clearRetaggedFieldSettings = (previous, next) => {
  const before = previous?.document?.tempFields?.formFields;
  const temp = next?.document?.tempFields;
  if (
    !Array.isArray(before) ||
    !Array.isArray(temp?.formFields) ||
    before === temp.formFields
  )
    return next;
  const key = (field) => field?.field_instance_id || field?.id || field?.name;
  const oldByKey = new Map(
    before
      .filter((field) => key(field) != null)
      .map((field) => [key(field), field]),
  );
  const changed = new Map();
  const formFields = temp.formFields.map((field) => {
    const old = oldByKey.get(key(field));
    if (!old || old.field_id === field.field_id || field.field_settings == null)
      return field;
    const updated = { ...field, field_settings: {} };
    changed.set(key(field), updated);
    return updated;
  });
  if (!changed.size) return next;
  const refresh = (field) => changed.get(key(field)) || field;
  return {
    ...next,
    document: {
      ...next.document,
      tempFields: {
        ...temp,
        formFields,
        selectedFormField: refresh(temp.selectedFormField),
        ...(Array.isArray(temp.selectedExtraFields)
          ? {
              selectedExtraFields: temp.selectedExtraFields.map(refresh),
            }
          : {}),
      },
    },
  };
};

export const mergePermitGroupOverrides = (fields = []) => {
  const isGroup = (field) =>
    ["checkboxGroup", "selectGroup"].includes(field?.field_type ?? field?.type);
  const zero = (value) =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value)) &&
    Number(value) === 0;
  const isMetadataShape = (field) =>
    field?.hidden === true &&
    [
      [field.x, field.rect?.X],
      [field.y, field.rect?.Y],
      [field.width, field.rect?.W],
      [field.height, field.rect?.H],
    ].every(([value, rectValue]) => zero(value ?? rectValue));
  const groupIds = new Set(
    fields
      .filter((field) => isGroup(field) && !isMetadataShape(field))
      .map((field) => field.field_id)
      .filter(Boolean),
  );
  const groupsWithOptions = new Set(
    fields
      .filter((field) => isGroup(field) && field.options?.length > 0)
      .map((field) => field.field_id),
  );
  const isOverride = (field) =>
    groupIds.has(field?.field_id) &&
    (isMetadataShape(field) ||
      (!isGroup(field) &&
        ["text", "PDFTextField"].includes(field?.field_type ?? field?.type)) ||
      (groupsWithOptions.has(field.field_id) &&
        !field.options?.length &&
        field.field_settings != null));
  const overrides = new Map();
  fields.forEach((field) => {
    if (!isOverride(field)) return;
    const previous = overrides.get(field.field_id) || {};
    const label = field.field_label ?? field.label;
    overrides.set(field.field_id, {
      ...previous,
      field_settings: {
        ...getFieldSettings(previous),
        ...getFieldSettings(field),
      },
      ...(typeof label === "string" && label.trim() ? { label } : {}),
    });
  });
  return fields
    .filter((field) => !isOverride(field))
    .map((field) => {
      const override = isGroup(field) ? overrides.get(field.field_id) : null;
      if (!override) return applyFieldSettings(field);
      return applyFieldSettings({
        ...field,
        field_settings: {
          ...getFieldSettings(field),
          ...getFieldSettings(override),
        },
        ...(override.label != null
          ? {
              field_label: override.label,
              label: override.label,
              title: override.label,
            }
          : {}),
      });
    });
};
